import React, { useState, useEffect, useContext } from 'react';
import _ from 'lodash';
import { useParams, useNavigate } from 'react-router-dom';
import { PageContext } from '../pages/components/page';
import { collectGarbage, setHelpPage } from '../configTdm';
import { hasPermissions } from '../site/userService';
import { AdminService } from './AdminService';
import { getProperty, overrideConfig } from '../fsConfig';
import { spinnerHide, spinnerShow } from '../lib/spinner';
import { decorateUser } from '../user';
import styles from './admin.module.css';
import cn from 'classnames';
import AccountAdminUserComponent from './AccountAdminUserComponent';
import SearchTextComponent from './SearchText';
import { SUPER_ADMIN } from '../../js/constants';

interface User {
    id: string;
    user: {
        aav1UserId: string;
        accountId: string;
        roles: string[];
        entitlements?: {
            orgNodeName: string;
        };
    };
    userFullObj: {
        userName: string;
        email: string;
        legacyId: string;
        createdAt: string;
        accountId: string;
        active: boolean;
        selectedRoles: string[];
        roles: string;
    };
    hidden: boolean;
}
interface Account {
    id?: string;
    account?: {
        name: string;
    };
    entitlements?: Record<string, unknown>;
}

interface UserProps {
    id?: string;
    userName: string;
    email: string;
    active: boolean;
    legacyId: string;
    createdAt: string;
    accountId: string;
    selectedRoles: string[];
    roles: string;
}

const AccountsAdminComponent: React.FC = () => {
    const { accountId } = useParams<{ accountId: string }>();
    const { pushAlert } = useContext(PageContext);
    const [users, setUsers] = useState<User[]>([]);
    const [account, setAccount] = useState<Account | null>(null);
    const permissions = getProperty('session', {}).permissions;
    const superAdmin = (permissions || []).includes(SUPER_ADMIN);
    const [selectedUserObj, setSelectedUserObj] = useState<UserProps>({
        userName: '',
        email: '',
        active: false,
        legacyId: '',
        createdAt: '',
        accountId: '',
        selectedRoles: [],
        roles: ''
    });
    const [userCaller, setUserCaller] = useState<string>('');
    const [refresh, setRefresh] = useState<boolean>(false);
    const navigate = useNavigate();

    useEffect(() => {
        setHelpPage('helpAdmin');
        if (accountId) {
            refreshUsers(accountId);
            refreshAccount(accountId);
        }
        return () => {
            setUsers([]);
            setAccount(null);
            collectGarbage();
        };
    }, [accountId]);

    const canSudo = () => hasPermissions(['can-sudo']);

    const canSudoAs = (roles: string[]) => !roles.includes('administrator');

    const refreshAccount = async (id: string) => {
        try {
            spinnerShow();
            const resp = await AdminService.getAccount(id);
            if (resp.status === 200 && resp.data) {
                setAccount(resp.data);
                const entitlementsResp = await AdminService.getAccountEntitlements(resp.data.id);
                if (entitlementsResp.status === 200 && entitlementsResp.data) {
                    setAccount((prevAccount) => ({
                        ...prevAccount,
                        entitlements: entitlementsResp.data
                    }));
                }
            }
        } catch (error) {
            pushAlert('danger', 'Error refreshing account.');
        } finally {
            spinnerHide();
        }
    };

    const refreshUsers = async (id: string, flag?: boolean, callback?: Function) => {
        try {
            spinnerShow();
            await AdminService.refreshUsers(users, id);
            setUsers([...users]);
            setRefresh(flag ?? false);
            callback && callback();
        } catch (error) {
            pushAlert('danger', 'Error refreshing users.');
        } finally {
            spinnerHide();
        }
    };

    const sudoAs = async (userId: string) => {
        try {
            spinnerShow();
            const sudoAsUsernameData = await AdminService.sudoAs(userId);
            const aav1Host = getProperty('aav1Host', '');
            const development = getProperty('development', true);
            const newUser = decorateUser(sudoAsUsernameData.data);
            overrideConfig({
                session: newUser,
                aav1Host: aav1Host,
                development: development,
            });
            spinnerHide();
            navigate('/home');
        } catch (error) {
            pushAlert('danger', 'Error performing sudo.');
        }
    };

    return (
        <>
            <AccountAdminUserComponent
                caller={userCaller}
                modalClose={() => setUserCaller('')}
                selectedAccount={{
                    id: account?.id || '',
                    account: { name: account?.account?.name || '' },
                    entitlements: { assignedOrgNodeId: (account?.entitlements?.assignedOrgNodeId || '') as string }
                }}
                refreshUsers={(callback?: Function) => refreshUsers(account?.id || '', true, callback)}
                selectedUserObj={selectedUserObj}
            ></AccountAdminUserComponent>
            <div className="content">
                <div className="admin">
                    <h2>{account?.account?.name}</h2>
                    <div className={cn(styles.newUserBtn)}>
                        <div className={cn(styles.searchText)}>
                            <SearchTextComponent listData={users} callback={(listData: User[]) => {
                                setUsers([...listData]);
                            }} refresh={refresh} updateRefresh={() => setRefresh(false)}></SearchTextComponent>
                        </div>
                        {superAdmin && <button
                            type="button"
                            className="btn btn-warning"
                            onClick={() => setUserCaller('add')}
                        >
                            NEW USER
                        </button>}
                    </div>
                    <div className="panel panel-default">
                        <div className="panel-body">
                            <table
                                id="usersGrid"
                                className="table table-striped table-hover table-condensed"
                            >
                                <colgroup>
                                    <col className="col2" />
                                    <col className="col3" />
                                    <col className="col4" />
                                    <col className="col5" />
                                    <col className="col6" />
                                    <col className="col7" />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>AAv1 Id</th>
                                        <th>Account</th>
                                        <th>Roles</th>
                                        <th>Entitlements</th>
                                        {canSudo() && <th></th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map(user => (
                                        !user.hidden && (
                                            <tr key={user.id}>
                                                <td>{user.id}</td>
                                                <td>{user.user.aav1UserId}</td>
                                                <td>{user.user.accountId}</td>
                                                <td>{user.user.roles.join(', ')}</td>
                                                <td>{user.user.entitlements?.orgNodeName}</td>
                                                <td>
                                                    {superAdmin && <button
                                                        type="button"
                                                        className="btn btn-warning"
                                                        onClick={() => {
                                                            setUserCaller('edit');
                                                            setSelectedUserObj(user.userFullObj);
                                                        }}
                                                    >
                                                        EDIT
                                                    </button>}
                                                    {canSudo() && canSudoAs(user.user.roles) && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-warning margin-left-5"
                                                            onClick={() => sudoAs(user.user.aav1UserId)}
                                                        >
                                                            SUDO
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        )
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="panel-footer">
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => (accountId ? refreshUsers(accountId, true) : {})}
                            >
                                Refresh
                            </button>
                            <span className={cn(styles.userCount)}>{users.filter(userObj => !userObj.hidden).length} users</span>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AccountsAdminComponent;